import { forDesktop, forIPhone5 } from '@westwing/ui-kit/breakPoints';
import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import ActionButton from '@westwing/ui-kit/ActionButton';
import { body2 } from '@westwing/ui-kit/typography';
import useOverlayOutsideClick from '@westwing/ui-kit/Tools/hooks/useOverlayOutsideClick';
import { RotatingSpinner } from '@westwing/ui-kit/Spinner';
import { useClubContext } from './hooks/ClubContext';
import { CartRestoreResponse } from './types/cartReminderTypes';
import { ClubLink } from './types/clubLinksTypes';
import { parseSeconds } from './tray/CartIcon';
import { generateWWEvents } from './WWEvents';
import { useHeaderContext } from 'Components/HeaderContext';

const IconWrapper = styled.div`
    text-align: center;
    margin-bottom: 13px;
`;

const StyledDisclaimerIcon = styled.i`
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.colors.charcoal100};
    background-color: ${({ theme }) => theme.colors.charcoal100};
    width: 35px;
    height: 35px;
    line-height: 35px;
    box-sizing: border-box;
    font-style: normal;
    display: inline-flex;
    text-align: center;
    justify-content: center;
    font-size: 20px;

    &:before {
        content: '!';
        color: ${({ theme }) => theme.colors.white};
    }
`;

const ReminderText = styled.div`
    text-align: center;
    font-family: ${({ theme }) => theme.fonts.fontBrandonRegular};
    color: ${({ theme }) => theme.colors.charcoal100};
    font-size: 14px;
    margin-bottom: 15px;

    time {
        color: ${({ theme }) => theme.colors.red100};
        font-family: ${({ theme }) => theme.fonts.fontBrandonMedium};
    }
`;

const ActionButtons = styled.div<{ isTranslationLong: boolean }>`
    display: flex;
    justify-content: space-between;
    text-align: center;
    flex-direction: ${({ isTranslationLong }) => (isTranslationLong ? 'column' : 'row')};
    gap: 10px;

    ${forIPhone5`
        flex-direction: column;
    `}
`;

const CartReminderButton = styled(ActionButton)`
    height: 44px;
    line-height: 44px;
    box-sizing: border-box;
`;

const Wrapper = styled.div`
    ${body2}
    position: absolute;
    background-color: ${({ theme }) => theme.colors.charcoal3};
    border-bottom: 1px solid ${({ theme }) => theme.colors.charcoal20};
    width: 100vw;
    right: 0;
    top: ${({ theme }) => theme.owwHeader.mobileRowHeight}px;
    ${forDesktop`
        display: none;
    `}
`;

const CartReminderBody = styled.div`
    padding: 27px 20px 20px;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
`;

const StyledSpinner = styled(RotatingSpinner)`
    height: 30px;
    width: 30px;
`;

export const restoreCart = async (): Promise<number> => {
    try {
        const response = await fetch(`/c/cart/restore/`, {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        const { data }: { data: CartRestoreResponse } = await response.json();

        if (data.success) {
            if (data.code.expired) {
                throw new Error("Restoration isn't possible. Cart is already expired");
            }

            return data.code.seconds_left;
        }

        throw new Error("API didn't return a successful response");
    } catch (e) {
        throw new Error("Couldn't make the request");
    }
};

const CartReminder = ({
    remainingTime,
    remindAt,
    onCartRestore,
}: {
    remainingTime: number;
    remindAt: number;
    onCartRestore: (secondsLeft: number) => void;
}): JSX.Element => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [timer, setTimer] = React.useState('');

    const { t } = useHeaderContext();
    const translatedRenew = t('OwwHeader_Renew now');
    const translatedCheckoutText = t('OwwHeader_Proceed to Checkout');
    const { links } = useClubContext();
    const { logger } = useHeaderContext();

    const outsideClickRef = useOverlayOutsideClick(() => setIsOpen(false));

    const updateTimer = React.useCallback((seconds: number) => {
        const time = seconds === 0 ? '' : parseSeconds(seconds);
        setTimer(time);
    }, []);

    useEffect(() => {
        updateTimer(remainingTime);
    }, [remainingTime, updateTimer]);

    const openReminder = useCallback(() => {
        if (!isOpen) {
            setIsOpen(true);
        }
    }, [isOpen]);

    useEffect(() => {
        const WWEvent = generateWWEvents();
        WWEvent.push({
            event: 'CART_REMIND',
            listener: openReminder,
        });
        return () => WWEvent.remove('CART_REMIND', openReminder);
    }, [openReminder]);

    useEffect(() => {
        if (remainingTime === remindAt) {
            openReminder();
        }

        if (remainingTime <= 0) {
            setIsOpen(false);
        }
    }, [remindAt, remainingTime, openReminder]);

    const handleCartRestore = async () => {
        setIsLoading(true);
        try {
            const secondsLeft = await restoreCart();
            onCartRestore(secondsLeft);
        } catch (e) {
            if (e instanceof Error) {
                logger.error(e.message);
            }
        }
        setIsLoading(false);
        setIsOpen(false);
    };

    return (
        <>
            {isOpen && (
                <Wrapper ref={outsideClickRef} data-testid="cart-reminder">
                    <CartReminderBody>
                        {isLoading ? (
                            <LoadingContainer>
                                <StyledSpinner />
                            </LoadingContainer>
                        ) : (
                            <>
                                <IconWrapper>
                                    <StyledDisclaimerIcon />
                                </IconWrapper>
                                <ReminderText
                                    data-testid="cart-reminder-text"
                                    dangerouslySetInnerHTML={{
                                        __html: t('OwwHeader_Your cart expires in %s', [`<time>${timer}</time>`]),
                                    }}
                                />
                                <ActionButtons
                                    isTranslationLong={
                                        translatedRenew.length > 20 || translatedCheckoutText.length > 20
                                    }
                                >
                                    <CartReminderButton
                                        data-testid="cart-reminder-cart-restore"
                                        kind="primary"
                                        onClick={handleCartRestore}
                                    >
                                        {translatedRenew}
                                    </CartReminderButton>
                                    <CartReminderButton
                                        data-testid="cart-reminder-proceed-to-checkout"
                                        as="a"
                                        kind="secondary"
                                        href={`${links[ClubLink.CART].path}`}
                                    >
                                        {translatedCheckoutText}
                                    </CartReminderButton>
                                </ActionButtons>
                            </>
                        )}
                    </CartReminderBody>
                </Wrapper>
            )}
        </>
    );
};

export default CartReminder;
